import { Component, OnInit, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core'

import { Subscription, timer } from 'rxjs'
import { ShellToast, UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { datadogRum } from '@datadog/browser-rum'
import { NavigationEnd, Router } from '@angular/router'
import { LoggerService } from 'src/app/account/services/logger.service'
import { COPY_DETAILS, ErrorAction, PresentedError } from 'src/utils/presented-error'

/** Longest the toast stack gets before the oldest dismissable entry is dropped. */
const MAX_VISIBLE_TOASTS = 3
const SUCCESS_VISIBLE_MS = 1500
const ERROR_VISIBLE_MS = 5000
/** Must match the fadeout animation duration in the stylesheet. */
const FADE_MS = 500

/**
 * Actions the toast can actually carry out. A toast has no application, job or dialog
 * context, so anything needing one would render a button that only dismisses itself — worse
 * than no button. Those are stripped, and Copy Details is guaranteed so the rule that an
 * error never dead-ends still holds.
 *
 * Anchored surfaces keep the full action set; this is a toast-only narrowing.
 */
const TOAST_ACTIONABLE: ReadonlySet<string> = new Set([
  'copy-details', // handled inside arp-error-block
  'contact-support', // href
  'contact-account-team', // href
  'email-support', // href
  'reload-retry',
  'refresh',
  'retry-now',
  'sign-in',
  'back-to-applications',
  'resend-verification', // runs the call site's own remedy, below
])

/** Actions only the call site's `onAction` can carry out, so they need one to render. */
const NEEDS_CALL_SITE: ReadonlySet<string> = new Set(['resend-verification'])

interface QueuedToast {
  id: number
  kind: 'success' | 'error'
  message?: string
  presented?: PresentedError
  sticky: boolean
  leaving: boolean
  onAction?: (action: ErrorAction) => void
  hideTimer?: Subscription
  fadeTimer?: Subscription
}

@Component({
  selector: 'app-root',
  templateUrl: './ui-shell.component.html',
  styleUrls: ['./ui-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UiShell implements OnInit {
  /** Oldest first, so the newest lands nearest the bottom-right corner users already watch. */
  toasts: QueuedToast[] = []

  private nextToastId = 0

  constructor(
    private uiService: UiService,
    private router: Router,
    private logger: LoggerService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        datadogRum.startView(event.urlAfterRedirects) // Track route changes
        this.logger.info(`User navigated to: ${event.urlAfterRedirects}`)
      }
    })
  }

  ngOnInit() {
    // Initialize font loading optimization
    this.initializeFontLoading()

    this.uiService.toastStream.subscribe((toast: ShellToast) => this.enqueue(toast))
  }

  /**
   * A second failure must never silently replace the first, so toasts stack. Persistence
   * decides the lifetime: anything the user has to act on or read is pinned, because when a
   * toast is showing there is no anchored surface left to go back to.
   */
  private enqueue(toast: ShellToast) {
    if (toast.kind === 'error') {
      console.error(toast.presented.detail)
    }

    // A polling app hits the same failure once per request, so an unreachable server or a
    // repeated 403 would otherwise fill the stack with identical entries — and with pinned
    // toasts that pile never drains. Collapse onto the existing one instead.
    const duplicate = this.findDuplicate(toast)
    if (duplicate) {
      this.restartLifetime(duplicate)
      return
    }

    const sticky = toast.kind === 'error' && this.isPinned(toast.presented)

    const queued: QueuedToast = {
      id: this.nextToastId++,
      kind: toast.kind,
      message: toast.kind === 'success' ? toast.message : undefined,
      presented: toast.kind === 'error' ? this.withToastActions(toast.presented, !!toast.onAction) : undefined,
      onAction: toast.kind === 'error' ? toast.onAction : undefined,
      sticky,
      leaving: false,
    }

    this.toasts = [...this.toasts, queued]
    this.evictOverflow()

    if (!sticky) {
      queued.hideTimer = timer(this.visibleMsFor(queued)).subscribe(() => this.beginLeaving(queued))
    }
  }

  /** Drops actions this surface cannot perform, keeping Copy Details as the floor. */
  private withToastActions(presented: PresentedError, hasOnAction: boolean): PresentedError {
    // A throttled action renders while it counts down and goes live at zero, so it cannot be
    // dropped for being disabled the moment it arrives.
    const actions = presented.actions.filter(
      (action) =>
        TOAST_ACTIONABLE.has(action.id) &&
        (hasOnAction || !NEEDS_CALL_SITE.has(action.id)) &&
        (!action.disabled || (action.countdownSeconds ?? 0) > 0)
    )
    return { ...presented, actions: actions.length ? actions : [COPY_DETAILS] }
  }

  /** Same title and detail counts as the same condition; the reference timestamp differs. */
  private findDuplicate(toast: ShellToast): QueuedToast | undefined {
    return this.toasts.find((existing) => {
      if (existing.leaving || existing.kind !== toast.kind) return false
      return toast.kind === 'success'
        ? existing.message === toast.message
        : existing.presented.title === toast.presented.title && existing.presented.detail === toast.presented.detail
    })
  }

  /** A recurrence means the condition still holds, so a timed toast earns its full window again. */
  private restartLifetime(queued: QueuedToast) {
    if (queued.sticky) return
    if (subscriptionActive(queued.hideTimer)) queued.hideTimer.unsubscribe()
    queued.hideTimer = timer(this.visibleMsFor(queued)).subscribe(() => this.beginLeaving(queued))
  }

  /**
   * Only a plain timed dismissal expires. `self-clearing` is pinned too: it promises the
   * system will clear the condition, but the signals that would — connectivity state and the
   * recovery-point auto-retry — don't exist yet, so a timer would just hide a live problem.
   * Flip those back to timed when their clear signal lands.
   */
  private isPinned(presented: PresentedError): boolean {
    return typeof presented.persistence === 'string'
  }

  private visibleMsFor(queued: QueuedToast): number {
    if (queued.kind === 'success') return SUCCESS_VISIBLE_MS
    const persistence = queued.presented?.persistence
    if (persistence && typeof persistence === 'object') return persistence.ms
    return ERROR_VISIBLE_MS
  }

  /** Drops the oldest dismissable toast first; only sheds a pinned one when all are pinned. */
  private evictOverflow() {
    while (this.toasts.length > MAX_VISIBLE_TOASTS) {
      const victim = this.toasts.find((toast) => !toast.sticky && !toast.leaving) ?? this.toasts[0]
      this.remove(victim)
    }
  }

  private beginLeaving(queued: QueuedToast) {
    if (queued.leaving) return
    queued.leaving = true
    this.toasts = [...this.toasts]
    queued.fadeTimer = timer(FADE_MS).subscribe(() => this.remove(queued))
  }

  private remove(queued: QueuedToast) {
    if (subscriptionActive(queued.hideTimer)) queued.hideTimer.unsubscribe()
    if (subscriptionActive(queued.fadeTimer)) queued.fadeTimer.unsubscribe()
    this.toasts = this.toasts.filter((toast) => toast !== queued)
  }

  dismissToast(queued: QueuedToast) {
    this.beginLeaving(queued)
  }

  /**
   * Carries out the action. Only ids in `TOAST_ACTIONABLE` reach here, so every button a
   * toast renders does something — a reload, a navigation, or a copy handled by the block.
   */
  onToastAction(queued: QueuedToast, action: ErrorAction) {
    switch (action.id) {
      case 'reload-retry':
      case 'refresh':
      case 'retry-now':
        // Nothing on the page can be trusted after these failures, and the toast has no
        // narrower scope to re-fetch.
        window.location.reload()
        return

      case 'sign-in':
        window.location.href = this.uiService.buildLoginUrl()
        return

      case 'back-to-applications':
        this.router.navigateByUrl('/')
        break

      default:
        if (action.route) this.router.navigateByUrl(action.route)
        // The call site sent its remedy with the failure, which is the only way a toast can
        // re-run work owned by a component the shell cannot reach.
        queued.onAction?.(action)
    }

    this.beginLeaving(queued)
  }

  toastTrackBy(_index: number, toast: QueuedToast): number {
    return toast.id
  }

  private initializeFontLoading() {
    // Add fonts-loading class initially
    this.document.body.classList.add('fonts-loading')

    // Modern approach: Use Font Loading API
    if (this.document.fonts) {
      // Check if fonts are already loaded
      this.document.fonts.ready.then(() => {
        this.checkSpecificFonts()
      })
    } else {
      // Fallback for very old browsers
      this.fallbackFontCheck()
    }
  }

  private checkSpecificFonts() {
    try {
      // Check if Source Sans 3 is available using FontFace.load()
      this.document.fonts
        .load('12px "Source Sans 3"')
        .then(() => {
          this.onFontsLoaded()
        })
        .catch(() => {
          // Font failed to load, use fallback timing
          this.fallbackFontCheck()
        })
    } catch (error) {
      // FontFace.load not supported, use fallback
      this.fallbackFontCheck()
    }
  }

  private fallbackFontCheck() {
    // Simple timeout for older browsers - most fonts load within 2-3 seconds
    setTimeout(() => {
      this.onFontsLoaded()
    }, 3000)
  }

  private onFontsLoaded() {
    this.document.body.classList.remove('fonts-loading')
    this.document.body.classList.add('fonts-loaded')
  }
}
